// The snooker room around the table, built in code (replaces the stars.jpg skybox and Models/Snooker/Cenario).
// Rendering only. Sizes and positions come from roomLayout.ts, which roomLayout.test.ts checks against the camera.
//
//   floor: oak boards (textures/room/floor.jpg)
//   walls: mahogany wainscoting (a profiled skirting board, raised and fielded panels framed by mouldings, a dado
//     rail with a bead) with a claret damask wallpaper above and a painted cornice; a door; a ceiling
//   the canopy lamp over the table (visual only: it fades out as the camera rises towards it, see lampOpacity)
//   the scoreboard and the club's sign (ONLINE SNOOKER CLUB) on the far wall, a cue rack with six cues and two chairs
//   in striped upholstery on the right wall, a buttoned leather bench on the left wall, a landscape painting and an
//   old club photograph in gilt frames on the back wall. The wall pieces hang low and out to the sides, clear of the
//   score panel that the HUD shows at the top centre of the view (QA F4).
//
// Graphics quality (quality.ts): Low leaves out the chairs, the cue rack, the painting, the photograph and the mouldings (plain
// skirting, dado rail and panels, as before), halves the subdivisions of the large surfaces and uses half-resolution
// textures; the bench is made of plain boxes. On High the chairs and the bench have rounded edges (roundedBox.ts) and
// soft cushions: rounded, puffed in the middle, a darker seam along the edges, and on the buttoned leather a gentle
// dimple at each button of the texture. Same footprint on both levels (roomLayout.ts).
//
// Lighting: nothing here adds a light or a shadow. All of the room uses unlit MeshBasicMaterial with the light
// baked into vertex colours at build time: a pool of light from the lamp's diffuser (cosine falloff), the table's
// soft shadow on the floor, darkening into the corners, and a constant ambient so the room is never black. That is
// cheaper to draw than the lit materials of the old floor model, and the table and balls are lit exactly as before.
// Textures: floor.jpg (~65 KB) and the table's wood.jpg; the wallpaper, the upholstery, the scoreboard, the sign, the
// painting and the photograph are drawn on canvases at start-up (roomTextures.ts), nothing more to download.
import {
    FURNITURE, LAMP, ROOM, WAINSCOT, lampOpacity, V3,
} from "./roomLayout";
import {TABLE} from "./shared/rules";
import {RAIL_WIDTH} from "./tableShapes";
import {QUALITY_PROFILES, QualityProfile} from "./quality";
import {canvasTexture, imageTexture} from "./textures";
import {drawPainting, drawPhoto, drawScoreboard, drawSign, drawStripeFabric, drawTufted, drawWallpaper} from "./roomTextures";
import {FaceName, RoundedBoxOptions, roundedBox} from "./roundedBox";

declare const THREE: any;

type UV = [number, number];
type RGB = [number, number, number];

// ---- baked lighting ----

const AMBIENT = 0.54;     // everywhere, before the corners' darkening
const LAMP_GAIN = 1.25;
const LAMP_FALLOFF = 15;  // distance at which the lamp's light has halved
const TABLE_TOP_Y = 7.6;
const TABLE_RECT = {
    xMin: TABLE.xMin - RAIL_WIDTH, xMax: TABLE.xMax + RAIL_WIDTH,
    zMin: TABLE.yMin - RAIL_WIDTH, zMax: TABLE.yMax + RAIL_WIDTH,
};
const LIGHT_SAMPLES: V3[] = [];
for (const fx of [-0.75, -0.25, 0.25, 0.75]) {
    for (const fz of [-0.75, -0.25, 0.25, 0.75]) {
        LIGHT_SAMPLES.push([LAMP.cx + fx * LAMP.halfX, LAMP.bottom + 0.3, LAMP.cz + fz * LAMP.halfZ]);
    }
}

function tableBlocks(p: V3, s: V3): boolean {
    if (p[1] >= TABLE_TOP_Y - 0.6) return false;
    const t = (TABLE_TOP_Y - p[1]) / (s[1] - p[1]);
    const x = p[0] + (s[0] - p[0]) * t, z = p[2] + (s[2] - p[2]) * t;
    return x > TABLE_RECT.xMin && x < TABLE_RECT.xMax && z > TABLE_RECT.zMin && z < TABLE_RECT.zMax;
}

/** Darkening near the room's other surfaces (cheap ambient occlusion). */
function corners(p: V3): number {
    let k = 1;
    const near = (d: number, depth: number, width: number) => { if (d > 0.02) k *= 1 - depth * Math.exp(-d / width); };
    near(p[0] - ROOM.xMin, 0.3, 2.5); near(ROOM.xMax - p[0], 0.3, 2.5);
    near(p[2] - ROOM.zMin, 0.3, 2.5); near(ROOM.zMax - p[2], 0.3, 2.5);
    near(p[1] - ROOM.floor, 0.3, 2.0); near(ROOM.ceiling - p[1], 0.25, 2.5);
    // under and around the table, the floor gets less of the room's light
    if (p[1] < TABLE_TOP_Y - 0.6) {
        const dx = Math.max(TABLE_RECT.xMin - p[0], 0, p[0] - TABLE_RECT.xMax);
        const dz = Math.max(TABLE_RECT.zMin - p[2], 0, p[2] - TABLE_RECT.zMax);
        k *= 1 - 0.4 * Math.max(0, 1 - Math.hypot(dx, dz) / 3.5);
    }
    return k;
}

/** Brightness of a surface point with normal n. */
export function shade(p: V3, n: V3): number {
    let direct = 0;
    for (const s of LIGHT_SAMPLES) {
        const dx = s[0] - p[0], dy = s[1] - p[1], dz = s[2] - p[2];
        const d = Math.hypot(dx, dy, dz) || 1;
        const cosSurface = (n[0] * dx + n[1] * dy + n[2] * dz) / d;
        const cosEmit = dy / d;   // the diffuser shines downwards
        if (cosSurface <= 0 || cosEmit <= 0) continue;
        const blocked = tableBlocks(p, s) ? 0.12 : 1;
        direct += cosSurface * cosEmit * blocked / (1 + (d / LAMP_FALLOFF) * (d / LAMP_FALLOFF));
    }
    direct /= LIGHT_SAMPLES.length;
    return AMBIENT * corners(p) + LAMP_GAIN * direct;
}

// ---- geometry ----

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mul = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const norm = (a: V3): V3 => mul(a, 1 / (Math.hypot(a[0], a[1], a[2]) || 1));

export const MAT = {floor: 0, wallpaper: 1, wood: 2, plain: 3, scoreboard: 4, sign: 5, painting: 6, fabric: 7, tufted: 8, photo: 9};

interface SurfaceOpts {
    mat: number;
    tint?: RGB;
    /** world units per texture repeat along u and v; omit for 0..1 over the surface */
    tile?: [number, number];
    /** subdivisions, so the baked light can vary across a large surface */
    nu?: number; nv?: number;
    /** use this colour as is (no baked light), e.g. the lamp's diffuser */
    unlit?: boolean;
}

/** Collects quads with UVs, per-vertex colours (the baked light) and material indices into one THREE.Geometry. */
class RoomBuilder {
    geometry = new THREE.Geometry();
    faces = 0;

    /** subdivisions of large surfaces relative to High (QualityProfile.roomSubdivision) */
    constructor(readonly subdivision = 1) {}

    private tri(p: V3[], uv: UV[], c: RGB[], mat: number) {
        const g = this.geometry, base = g.vertices.length;
        for (const q of p) g.vertices.push(new THREE.Vector3(q[0], q[1], q[2]));
        const f = new THREE.Face3(base, base + 1, base + 2, undefined, undefined, mat);
        f.vertexColors = c.map(k => new THREE.Color().setRGB(k[0], k[1], k[2]));
        g.faces.push(f);
        g.faceVertexUvs[0].push(uv.map(t => new THREE.Vector2(t[0], t[1])));
        this.faces++;
    }

    /**
     * A flat rectangle from `origin` along u and v (full-length vectors), facing u x v, split into nu x nv quads.
     */
    surface(origin: V3, u: V3, v: V3, o: SurfaceOpts) {
        const nu = Math.max(1, Math.round((o.nu || 1) * this.subdivision)), nv = Math.max(1, Math.round((o.nv || 1) * this.subdivision));
        const tint = o.tint || [1, 1, 1];
        const n = norm(cross(u, v));
        const ul = Math.hypot(u[0], u[1], u[2]), vl = Math.hypot(v[0], v[1], v[2]);
        const uh = mul(u, 1 / ul), vh = mul(v, 1 / vl);
        const pt = (i: number, j: number) => add(origin, add(mul(u, i / nu), mul(v, j / nv)));
        const uvOf = (p: V3, i: number, j: number): UV => o.tile ? [dot(p, uh) / o.tile[0], dot(p, vh) / o.tile[1]] : [i / nu, j / nv];
        const colOf = (p: V3): RGB => {
            const s = o.unlit ? 1 : shade(p, n);
            return [tint[0] * s, tint[1] * s, tint[2] * s];
        };
        const P: V3[][] = [], UVs: UV[][] = [], C: RGB[][] = [];
        for (let i = 0; i <= nu; i++) {
            P.push([]); UVs.push([]); C.push([]);
            for (let j = 0; j <= nv; j++) {
                const p = pt(i, j);
                P[i].push(p); UVs[i].push(uvOf(p, i, j)); C[i].push(colOf(p));
            }
        }
        for (let i = 0; i < nu; i++) {
            for (let j = 0; j < nv; j++) {
                const a: [number, number] = [i, j], b: [number, number] = [i + 1, j], c: [number, number] = [i + 1, j + 1], d: [number, number] = [i, j + 1];
                const tri = (x: [number, number][]) => this.tri(x.map(k => P[k[0]][k[1]]), x.map(k => UVs[k[0]][k[1]]), x.map(k => C[k[0]][k[1]]), o.mat);
                tri([a, b, c]);
                tri([a, c, d]);
            }
        }
    }

    /**
     * An axis-aligned box with outward faces. `skip` leaves out faces by name (+x, -x, +y, -y, +z, -z), e.g. the
     * side against a wall; `perFace` overrides the options for one face.
     */
    box(min: V3, max: V3, o: SurfaceOpts, skip: string[] = [], perFace: {[face: string]: Partial<SurfaceOpts>} = {}) {
        const [x0, y0, z0] = min, [x1, y1, z1] = max;
        const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
        const faces: [string, V3, V3, V3][] = [
            ["+x", [x1, y0, z1], [0, 0, -dz], [0, dy, 0]],
            ["-x", [x0, y0, z0], [0, 0, dz], [0, dy, 0]],
            ["+z", [x0, y0, z1], [dx, 0, 0], [0, dy, 0]],
            ["-z", [x1, y0, z0], [-dx, 0, 0], [0, dy, 0]],
            ["+y", [x0, y1, z1], [dx, 0, 0], [0, 0, -dz]],
            ["-y", [x0, y0, z0], [dx, 0, 0], [0, 0, dz]],
        ];
        for (const [name, origin, u, v] of faces) {
            if (skip.includes(name)) continue;
            this.surface(origin, u, v, Object.assign({}, o, perFace[name] || {}));
        }
    }

    /**
     * A box with rounded edges (roundedBox.ts) with the light baked in from its smooth normals; `r.segments` 0 makes
     * the plain box(). `seamTint` darkens the points on the seams between faces (a cushion's piping).
     */
    rounded(min: V3, max: V3, o: SurfaceOpts, r: RoundedBoxOptions, seamTint = 1) {
        if (!(r.segments > 0)) {
            this.box(min, max, o, r.skip || []);
            return;
        }
        const m = roundedBox(min, max, r), tint = o.tint || [1, 1, 1];
        const col = m.positions.map((p, i): RGB => {
            const k = shade(p, m.normals[i]) * (m.seam[i] ? seamTint : 1);
            return [tint[0] * k, tint[1] * k, tint[2] * k];
        });
        const uv = m.uvs.map((t): UV => o.tile ? [t[0] / o.tile[0], t[1] / o.tile[1]] : t);
        for (const t of m.triangles) this.tri(t.map(i => m.positions[i]), t.map(i => uv[i]), t.map(i => col[i]), o.mat);
    }

    /**
     * A vertical turned shape (cue, post): profile is [radius, height, colour] from the bottom up, around (x, z).
     */
    lathe(x: number, z: number, y0: number, profile: [number, number, RGB][], segments: number, mat: number) {
        for (let k = 0; k < profile.length - 1; k++) {
            const [r0, h0, c0] = profile[k], [r1, h1] = profile[k + 1];
            for (let s = 0; s < segments; s++) {
                const a0 = 2 * Math.PI * s / segments, a1 = 2 * Math.PI * (s + 1) / segments;
                const P = (r: number, h: number, a: number): V3 => [x + r * Math.cos(a), y0 + h, z + r * Math.sin(a)];
                const quad = [P(r0, h0, a0), P(r0, h0, a1), P(r1, h1, a1), P(r1, h1, a0)];
                const n: V3 = norm([Math.cos((a0 + a1) / 2), (r0 - r1) / Math.max(1e-6, h1 - h0), Math.sin((a0 + a1) / 2)]);
                const col = quad.map(p => { const s2 = shade(p, n); return [c0[0] * s2, c0[1] * s2, c0[2] * s2] as RGB; });
                const uv: UV[] = [[s / segments, h0], [(s + 1) / segments, h0], [(s + 1) / segments, h1], [s / segments, h1]];
                // facing outwards: (p1 - p0) x (p3 - p0) points out for this order
                this.tri([quad[0], quad[3], quad[2]], [uv[0], uv[3], uv[2]], [col[0], col[3], col[2]], mat);
                this.tri([quad[0], quad[2], quad[1]], [uv[0], uv[2], uv[1]], [col[0], col[2], col[1]], mat);
            }
        }
    }

    build() {
        const g = this.geometry;
        if (g.computeCentroids) g.computeCentroids();
        g.computeFaceNormals();
        return g;
    }
}

// ---- the room ----

const WAINSCOT_TINT: RGB = [0.62, 0.55, 0.52];   // on the table's mahogany texture: darker, browner
const PANEL_TINT: RGB = [0.72, 0.64, 0.6];
const TRIM_TINT: RGB = [0.82, 0.74, 0.68];
const SKIRTING_TINT: RGB = [0.92, 0.82, 0.74];    // the mouldings' rounded edges catch more light
const FIELD_TINT: RGB = [0.8, 0.71, 0.66];
const CORNICE: RGB = [0.86, 0.82, 0.74];
const CEILING: RGB = [0.82, 0.79, 0.72];
const BRASS: RGB = [0.82, 0.66, 0.34];
const GILT: RGB = [0.62, 0.48, 0.24];
const GILT_LIGHT: RGB = [0.78, 0.62, 0.32];
const FURNITURE_WOOD: RGB = [0.78, 0.68, 0.62];

/** A wall seen from inside the room: from `a` to `b` along the floor, the room on its left looking from above. */
interface Wall { name: string; a: [number, number]; b: [number, number]; gaps: [number, number][]; }

function buildWalls(b: RoomBuilder, detail: boolean) {
    const R = ROOM, W = WAINSCOT;
    const door = FURNITURE.door;
    const walls: Wall[] = [
        // each runs so that u x up points into the room
        {name: "+z", a: [R.xMax, R.zMax], b: [R.xMin, R.zMax], gaps: []},
        {name: "-z", a: [R.xMin, R.zMin], b: [R.xMax, R.zMin], gaps: []},
        {name: "+x", a: [R.xMax, R.zMin], b: [R.xMax, R.zMax], gaps: []},
        // the door is on the -x wall: its distance along the wall from a
        {name: "-x", a: [R.xMin, R.zMax], b: [R.xMin, R.zMin], gaps: [[R.zMax - door.max[2], R.zMax - door.min[2]]]},
    ];
    for (const w of walls) {
        const L = Math.hypot(w.b[0] - w.a[0], w.b[1] - w.a[1]);
        const t: V3 = [(w.b[0] - w.a[0]) / L, 0, (w.b[1] - w.a[1]) / L];
        const inward = norm(cross(t, [0, 1, 0]));
        const at = (s: number, y: number, off: number): V3 => [w.a[0] + t[0] * s + inward[0] * off, y, w.a[1] + t[2] * s + inward[2] * off];
        // spans of the wall without gaps
        const spans: [number, number][] = [];
        let s0 = 0;
        for (const g of w.gaps) { spans.push([s0, g[0]]); s0 = g[1]; }
        spans.push([s0, L]);
        // wallpaper above the dado rail, the full length (the door covers its part)
        const top = R.ceiling - W.corniceHeight;
        b.surface(at(0, W.top, 0), mul(t, L), [0, top - W.top, 0], {mat: MAT.wallpaper, tile: [7, 7], nu: Math.ceil(L / 3), nv: 6});
        // wood behind the panels (and behind the door, hidden)
        b.surface(at(0, 0, 0), mul(t, L), [0, W.top, 0], {mat: MAT.wood, tint: WAINSCOT_TINT, tile: [6, 3], nu: Math.ceil(L / 3), nv: 3});
        // the cornice along the top
        b.surface(at(0, top, W.corniceDepth), mul(t, L), [0, W.corniceHeight * 0.45, -0], {mat: MAT.plain, tint: CORNICE, nu: Math.ceil(L / 4)});
        b.surface(at(0, top + W.corniceHeight * 0.45, W.corniceDepth), mul(t, L), mul(inward, -W.corniceDepth), {mat: MAT.plain, tint: CORNICE, nu: Math.ceil(L / 4)});
        b.surface(at(0, top + W.corniceHeight * 0.45, W.corniceDepth * 0.35), mul(t, L), [0, W.corniceHeight * 0.55, 0], {mat: MAT.plain, tint: CORNICE, nu: Math.ceil(L / 4)});
        b.surface(at(0, top, 0), mul(t, L), mul(inward, W.corniceDepth), {mat: MAT.plain, tint: CORNICE, nu: Math.ceil(L / 4)});
        for (const [p, q] of spans) {
            const len = q - p;
            if (len < 0.5) continue;
            const box = (s: number, e: number, y0: number, y1: number, depth: number, tint: RGB) => {
                const c0 = at(s, y0, 0), c1 = at(e, y1, depth);
                const min: V3 = [Math.min(c0[0], c1[0]), y0, Math.min(c0[2], c1[2])];
                const max: V3 = [Math.max(c0[0], c1[0]), y1, Math.max(c0[2], c1[2])];
                const back = w.name === "+z" ? "+z" : w.name === "-z" ? "-z" : w.name === "+x" ? "+x" : "-x";
                b.box(min, max, {mat: MAT.wood, tint, tile: [6, 3]}, [back, "-y"]);
            };
            const r0 = W.top - W.railHeight;
            if (detail) {
                // skirting: a plinth, a proud rounded moulding on it and a thin bead on top
                box(p, q, 0, W.skirtingHeight - 0.2, W.skirtingDepth, TRIM_TINT);
                box(p, q, W.skirtingHeight - 0.2, W.skirtingHeight - 0.06, W.skirtingDepth + 0.07, SKIRTING_TINT);
                box(p, q, W.skirtingHeight - 0.06, W.skirtingHeight + 0.06, W.skirtingDepth - 0.06, TRIM_TINT);
                // dado rail: the rail, a bead under it and a thin cap
                box(p, q, r0, W.top - 0.05, W.railDepth, TRIM_TINT);
                box(p, q, r0 - 0.14, r0, W.railDepth - 0.13, SKIRTING_TINT);
                box(p, q, W.top - 0.05, W.top + 0.03, W.railDepth + 0.04, SKIRTING_TINT);
            } else {
                box(p, q, 0, W.skirtingHeight, W.skirtingDepth, TRIM_TINT);
                box(p, q, r0, W.top, W.railDepth, TRIM_TINT);
            }
            // raised panels between them, about 6 wide
            const count = Math.max(1, Math.round(len / 6.5));
            const gap = 0.8, pw = (len - gap * (count + 1)) / count;
            const y0 = W.skirtingHeight + 0.8, y1 = r0 - 0.8;
            for (let k = 0; k < count; k++) {
                const s = p + gap + k * (pw + gap);
                if (!detail) { box(s, s + pw, y0, y1, W.panelDepth, PANEL_TINT); continue; }
                // a recessed panel, framed by a bolection moulding, with a raised field in the middle
                const m = 0.2;
                box(s, s + pw, y0, y1, 0.05, PANEL_TINT);
                box(s, s + pw, y0, y0 + m, W.panelDepth + 0.08, TRIM_TINT);
                box(s, s + pw, y1 - m, y1, W.panelDepth + 0.08, TRIM_TINT);
                box(s, s + m, y0 + m, y1 - m, W.panelDepth + 0.08, TRIM_TINT);
                box(s + pw - m, s + pw, y0 + m, y1 - m, W.panelDepth + 0.08, TRIM_TINT);
                box(s + 0.55, s + pw - 0.55, y0 + 0.55, y1 - 0.55, W.panelDepth + 0.02, FIELD_TINT);
                box(s + 0.75, s + pw - 0.75, y0 + 0.75, y1 - 0.75, W.panelDepth + 0.05, PANEL_TINT);
            }
        }
    }
}

function buildFloorAndCeiling(b: RoomBuilder) {
    const R = ROOM;
    const dx = R.xMax - R.xMin, dz = R.zMax - R.zMin;
    b.surface([R.xMin, R.floor, R.zMax], [dx, 0, 0], [0, 0, -dz], {mat: MAT.floor, tile: [9.6, 9.6], nu: 36, nv: 40});
    b.surface([R.xMin, R.ceiling, R.zMin], [dx, 0, 0], [0, 0, dz], {mat: MAT.plain, tint: CEILING, nu: 12, nv: 12});
    // the plate the lamp's rods hang from
    b.box([LAMP.cx - 1.6, R.ceiling - 0.25, LAMP.cz - 4.2], [LAMP.cx + 1.6, R.ceiling, LAMP.cz + 4.2], {mat: MAT.plain, tint: CORNICE}, ["+y"]);
}

function buildDoor(b: RoomBuilder) {
    const d = FURNITURE.door, x0 = d.min[0];
    const z0 = d.min[2], z1 = d.max[2], top = d.max[1];
    const arch = 0.7;
    const wood = (min: V3, max: V3, tint: RGB) => b.box(min, max, {mat: MAT.wood, tint, tile: [5, 3]}, ["-x", "-y"]);
    // architrave
    wood([x0, 0, z0], [x0 + 0.35, top, z0 + arch], TRIM_TINT);
    wood([x0, 0, z1 - arch], [x0 + 0.35, top, z1], TRIM_TINT);
    wood([x0, top - arch, z0 + arch], [x0 + 0.35, top, z1 - arch], TRIM_TINT);
    // the leaf, set back a little, with four raised panels
    wood([x0, 0, z0 + arch], [x0 + 0.12, top - arch, z1 - arch], WAINSCOT_TINT);
    const lz0 = z0 + arch + 0.8, lz1 = z1 - arch - 0.8, mid = (lz0 + lz1) / 2;
    for (const [y0, y1] of [[1.2, 8.6], [9.6, top - arch - 0.9]]) {
        wood([x0, y0, lz0], [x0 + 0.2, y1, mid - 0.4], PANEL_TINT);
        wood([x0, y0, mid + 0.4], [x0 + 0.2, y1, lz1], PANEL_TINT);
    }
    // handle
    b.box([x0 + 0.12, 8.7, z0 + arch + 0.5], [x0 + 0.45, 9.1, z0 + arch + 1.3], {mat: MAT.plain, tint: BRASS}, ["-x"]);
}

function buildScoreboard(b: RoomBuilder) {
    const s = FURNITURE.scoreboard;
    const [x0, y0] = [s.min[0], s.min[1]], [x1, y1, z1] = [s.max[0], s.max[1], s.max[2]];
    const zf = s.min[2];
    const frame = 0.5;
    const wood = (min: V3, max: V3) => b.box(min, max, {mat: MAT.wood, tint: FURNITURE_WOOD, tile: [5, 3]}, ["+z"]);
    wood([x0, y0, zf], [x1, y0 + frame, z1]);
    wood([x0, y1 - frame, zf], [x1, y1, z1]);
    wood([x0, y0 + frame, zf], [x0 + frame, y1 - frame, z1]);
    wood([x1 - frame, y0 + frame, zf], [x1, y1 - frame, z1]);
    // the board's face, a little behind the frame; the canvas is 2:1, so is this face (11 x 5.5)
    const fz = zf + 0.2;
    b.surface([x1 - frame, y0 + frame + 0.25, fz], [-(x1 - x0 - 2 * frame), 0, 0], [0, y1 - y0 - 2 * frame - 0.5, 0], {mat: MAT.scoreboard, tint: [1.15, 1.15, 1.15], nu: 2, nv: 2});
    // the filler strips above and below the face
    b.surface([x1 - frame, y0 + frame, fz], [-(x1 - x0 - 2 * frame), 0, 0], [0, 0.25, 0], {mat: MAT.wood, tint: WAINSCOT_TINT});
    b.surface([x1 - frame, y1 - frame - 0.25, fz], [-(x1 - x0 - 2 * frame), 0, 0], [0, 0.25, 0], {mat: MAT.wood, tint: WAINSCOT_TINT});
    // a small shelf for the chalk under it
    wood([x0 + 2, y0 - 0.35, zf - 0.3], [x1 - 2, y0, z1]);
    for (const x of [x0 + 3, x0 + 3.8, x1 - 3.8]) b.box([x - 0.2, y0, zf - 0.1], [x + 0.2, y0 + 0.3, zf + 0.3], {mat: MAT.plain, tint: [0.25, 0.42, 0.7]});
}

/**
 * A picture in a gilt frame on the back wall (-z, facing +z) or the far wall (+z, facing -z): `mat` is its canvas.
 */
function buildFramedPicture(b: RoomBuilder, box: {min: V3; max: V3}, mat: number, detail: boolean) {
    const [x0, y0, z0] = box.min, [x1, y1, z1] = box.max;
    const far = z1 >= ROOM.zMax - 1e-6;          // hangs on the far wall
    const back = far ? "+z" : "-z";               // the side against the wall
    const frame = 0.45;
    const gilt: SurfaceOpts = {mat: MAT.plain, tint: GILT};
    b.box([x0, y0, z0], [x1, y0 + frame, z1], gilt, [back]);
    b.box([x0, y1 - frame, z0], [x1, y1, z1], gilt, [back]);
    b.box([x0, y0 + frame, z0], [x0 + frame, y1 - frame, z1], gilt, [back]);
    b.box([x1 - frame, y0 + frame, z0], [x1, y1 - frame, z1], gilt, [back]);
    let inset = frame;
    if (detail) {
        // an inner lip, lighter and lower, stepping down to the picture
        const lip = 0.14, light: SurfaceOpts = {mat: MAT.plain, tint: GILT_LIGHT};
        const za = far ? z1 - (z1 - z0) * 0.65 : z0, zb = far ? z1 : z0 + (z1 - z0) * 0.65;
        b.box([x0 + frame, y0 + frame, za], [x1 - frame, y0 + frame + lip, zb], light, [back]);
        b.box([x0 + frame, y1 - frame - lip, za], [x1 - frame, y1 - frame, zb], light, [back]);
        b.box([x0 + frame, y0 + frame + lip, za], [x0 + frame + lip, y1 - frame - lip, zb], light, [back]);
        b.box([x1 - frame - lip, y0 + frame + lip, za], [x1 - frame, y1 - frame - lip, zb], light, [back]);
        inset += lip;
    }
    const w = x1 - x0 - 2 * inset, h = y1 - y0 - 2 * inset;
    // the canvas reads left to right as seen from the room
    if (far) b.surface([x1 - inset, y0 + inset, z1 - 0.12], [-w, 0, 0], [0, h, 0], {mat, tint: [1.1, 1.1, 1.1], nu: 2, nv: 2});
    else b.surface([x0 + inset, y0 + inset, z0 + 0.12], [w, 0, 0], [0, h, 0], {mat, tint: [1.1, 1.1, 1.1], nu: 2, nv: 2});
}

function buildCueRack(b: RoomBuilder) {
    const r = FURNITURE.cueRack;
    const xw = r.max[0], z0 = r.min[2], z1 = r.max[2];
    const wood = (min: V3, max: V3, tint = FURNITURE_WOOD) => b.box(min, max, {mat: MAT.wood, tint, tile: [5, 3]}, ["+x"]);
    // back board, the bottom tray, the clip bar and the crest
    wood([xw - 0.2, 1.0, z0 + 0.3], [xw, 13.4, z1 - 0.3], WAINSCOT_TINT);
    wood([xw - 1.1, 1.2, z0], [xw - 0.2, 1.8, z1]);
    wood([xw - 0.9, 11.4, z0], [xw - 0.2, 12.0, z1]);
    wood([xw - 0.5, 13.4, z0 - 0.2], [xw, 14.0, z1 + 0.2]);
    // six cues standing in the tray, held by the clip bar
    const butt: RGB = [0.24, 0.1, 0.05], ebony: RGB = [0.08, 0.06, 0.05], ash: RGB = [0.86, 0.7, 0.47];
    const ring: RGB = [0.9, 0.86, 0.72], ferrule: RGB = [0.95, 0.94, 0.9], tip: RGB = [0.25, 0.38, 0.62];
    for (let k = 0; k < 6; k++) {
        const z = z0 + 0.95 + k * (z1 - z0 - 1.9) / 5;
        const len = 12.3 + 0.12 * ((k * 7) % 3);
        const x = xw - 0.62;
        b.lathe(x, z, 1.8, [
            [0.17, 0, ebony], [0.17, 0.2, butt], [0.16, 4.2, ring], [0.152, 4.35, butt], [0.14, 5.4, ebony],
            [0.13, 5.6, ash], [0.075, len - 0.2, ferrule], [0.072, len - 0.05, tip], [0.068, len, tip], [0, len, tip],
        ], 8, MAT.plain);
        // wood grain is too fine to see at this size: the cue colours are in the vertex colours
    }
}

// Buttons of the tufted leather (roomTextures.drawTufted): with the bench's tiling every 1.5 along the face's u and v,
// plus the middle of each tile. A gentle dimple at each, for roundedBox's relief on the faces you sit on / lean on.
const TUFT_TILE = 1.5;
const FACE_UV: Partial<Record<FaceName, [[number, number], [number, number]]>> = {"+y": [[0, 1], [2, -1]], "+x": [[2, -1], [1, 1]]};
function tuftRelief(faces: FaceName[], depth = 0.08, sigma = 0.32) {
    return (face: FaceName, p: V3) => {
        const axes = FACE_UV[face];
        if (!axes || !faces.includes(face)) return null;
        const [[ua, us], [va, vs]] = axes;
        const U = us * p[ua], V = vs * p[va];
        let best: [number, number] = [Infinity, Infinity];
        for (const off of [0, TUFT_TILE / 2]) {
            const du = U - off - Math.round((U - off) / TUFT_TILE) * TUFT_TILE, dv = V - off - Math.round((V - off) / TUFT_TILE) * TUFT_TILE;
            if (du * du + dv * dv < best[0] * best[0] + best[1] * best[1]) best = [du, dv];
        }
        const [du, dv] = best, e = Math.exp(-(du * du + dv * dv) / (sigma * sigma));
        const gradient: V3 = [0, 0, 0];
        gradient[ua] = us * depth * 2 * du / (sigma * sigma) * e;
        gradient[va] = vs * depth * 2 * dv / (sigma * sigma) * e;
        return {height: -depth * e, gradient};
    };
}

const SEAM_TINT = 0.8;

function buildChair(b: RoomBuilder, c: {min: V3; max: V3}, bevel: number) {
    // facing the table (-x); the back is at +x, against the wall
    const [x0, , z0] = c.min, [x1, top, z1] = c.max;
    const small = bevel > 0 ? Math.max(2, bevel / 2) : 0;   // thin parts: fewer steps round their edges
    const wood = (min: V3, max: V3, radius: number, segments = small, skip: FaceName[] = []) =>
        b.rounded(min, max, {mat: MAT.wood, tint: FURNITURE_WOOD, tile: [4, 2]}, {radius, segments, skip});
    const cushion = (min: V3, max: V3, radius: number, bulge: Partial<Record<FaceName, number>>) =>
        b.rounded(min, max, {mat: MAT.fabric, tint: [1, 1, 1], tile: [1.6, 1.6]}, {radius, segments: bevel, spacing: 0.6, bulge, skip: ["-y"]}, SEAM_TINT);
    const seat = 4.0, leg = 0.38;
    for (const x of [x0 + 0.2, x1 - 0.2 - leg]) for (const z of [z0 + 0.2, z1 - 0.2 - leg]) wood([x, 0, z], [x + leg, seat - 0.5, z + leg], 0.08, small, ["-y"]);
    wood([x0, seat - 0.6, z0], [x1, seat - 0.3, z1], 0.1);
    // a soft seat: rounded, the top puffed up, the sides slightly out
    cushion([x0 + 0.1, seat - 0.3, z0 + 0.1], [x1 - 0.1, seat + 0.3, z1 - 0.1], 0.22, {"+y": 0.12, "+x": 0.04, "-x": 0.05, "+z": 0.04, "-z": 0.04});
    // back: two posts, a padded panel and a top rail
    for (const z of [z0 + 0.15, z1 - 0.15 - leg]) wood([x1 - 0.2 - leg, seat - 0.3, z], [x1 - 0.2, top - 0.3, z + leg], 0.08);
    cushion([x1 - 0.6, seat + 0.9, z0 + 0.55], [x1 - 0.25, top - 0.8, z1 - 0.55], 0.14, {"-x": 0.1, "+z": 0.03, "-z": 0.03});
    wood([x1 - 0.65, top - 0.8, z0 + 0.15], [x1 - 0.15, top, z1 - 0.15], 0.14, bevel);
    // arms
    for (const z of [z0, z1 - 0.35]) {
        wood([x0 + 0.3, seat + 1.5, z], [x1 - 0.5, seat + 1.85, z + 0.35], 0.13, bevel);
        wood([x0 + 0.4, seat + 0.3, z], [x0 + 0.75, seat + 1.5, z + 0.35], 0.08);
    }
}

function buildBench(b: RoomBuilder, bevel: number) {
    // against the -x wall, facing +x
    const c = FURNITURE.bench;
    const [x0, , z0] = c.min, [x1, top, z1] = c.max;
    const small = bevel > 0 ? Math.max(2, bevel / 2) : 0;
    const wood = (min: V3, max: V3, radius: number, skip: FaceName[] = [], segments = bevel) =>
        b.rounded(min, max, {mat: MAT.wood, tint: FURNITURE_WOOD, tile: [5, 2.5]}, {radius, segments, skip});
    const leather = (min: V3, max: V3, radius: number, bulge: Partial<Record<FaceName, number>>, face: FaceName, skip: FaceName[]) =>
        b.rounded(min, max, {mat: MAT.tufted, tint: [1.05, 1.05, 1.05], tile: [TUFT_TILE, TUFT_TILE]},
            {radius, segments: bevel, snap: TUFT_TILE / 4, snapFaces: [face], spacing: 0.75, bulge, skip, relief: tuftRelief([face])}, SEAM_TINT);
    const seat = 3.9;
    // base: a plinth and a panelled front
    wood([x0 + 0.3, 0, z0 + 0.5], [x1 - 0.2, 0.5, z1 - 0.5], 0.05, ["-y"], small);
    wood([x0 + 0.2, 0.5, z0 + 0.5], [x1, seat - 0.6, z1 - 0.5], 0.06, ["-y"], small);
    wood([x0, seat - 0.6, z0 + 0.4], [x1 + 0.05, seat - 0.3, z1 - 0.4], 0.1);
    // seat cushion and back cushion: buttoned leather, rounded and slightly puffed
    leather([x0 + 0.5, seat - 0.3, z0 + 0.5], [x1 - 0.05, seat + 0.35, z1 - 0.5], 0.22, {"+y": 0.08, "+x": 0.04}, "+y", ["-y", "-x"]);
    leather([x0, seat + 0.35, z0 + 0.5], [x0 + 0.65, top - 0.4, z1 - 0.5], 0.2, {"+x": 0.1, "+y": 0.03}, "+x", ["-y", "-x"]);
    wood([x0, top - 0.4, z0 + 0.4], [x0 + 0.8, top, z1 - 0.4], 0.15, ["-x"]);
    // ends
    for (const [za, zb] of [[z0, z0 + 0.5], [z1 - 0.5, z1]]) wood([x0, 0, za], [x1, seat + 1.6, zb], 0.2, ["-y"]);
}

/** The canopy: a dark green shade with brass trim, a warm diffuser underneath, on four rods from the ceiling. */
function buildLamp(b: RoomBuilder) {
    const L = LAMP, t = 0.14;
    const x0 = L.cx - L.halfX, x1 = L.cx + L.halfX, z0 = L.cz - L.halfZ, z1 = L.cz + L.halfZ;
    const outer: RGB = [0.1, 0.26, 0.15], inner: RGB = [0.62, 0.66, 0.52];
    const shadeOpts = (face: string, inward: string): Partial<SurfaceOpts> => ({tint: face === inward ? inner : outer});
    const side = (min: V3, max: V3, inward: string) => {
        const per: {[f: string]: Partial<SurfaceOpts>} = {};
        for (const f of ["+x", "-x", "+z", "-z", "+y", "-y"]) per[f] = shadeOpts(f, inward);
        b.box(min, max, {mat: 0, unlit: true, tint: outer}, [], per);
    };
    side([x0, L.bottom, z0], [x0 + t, L.top, z1], "+x");
    side([x1 - t, L.bottom, z0], [x1, L.top, z1], "-x");
    side([x0 + t, L.bottom, z0], [x1 - t, L.top, z0 + t], "+z");
    side([x0 + t, L.bottom, z1 - t], [x1 - t, L.top, z1], "-z");
    // top
    b.box([x0, L.top, z0], [x1, L.top + 0.12, z1], {mat: 0, unlit: true, tint: outer}, [], {"-y": {tint: inner}});
    // brass trim round the lower edge
    const trim = (min: V3, max: V3) => b.box(min, max, {mat: 0, unlit: true, tint: BRASS}, ["+y"]);
    trim([x0 - 0.05, L.bottom, z0 - 0.05], [x0 + t, L.bottom + 0.16, z1 + 0.05]);
    trim([x1 - t, L.bottom, z0 - 0.05], [x1 + 0.05, L.bottom + 0.16, z1 + 0.05]);
    trim([x0 + t, L.bottom, z0 - 0.05], [x1 - t, L.bottom + 0.16, z0 + t]);
    trim([x0 + t, L.bottom, z1 - t], [x1 - t, L.bottom + 0.16, z1 + 0.05]);
    // the diffuser, set up inside the shade, facing down; brighter than 1 so it reads as a light source
    b.surface([x0 + t, L.bottom + 0.35, z0 + t], [x1 - x0 - 2 * t, 0, 0], [0, 0, z1 - z0 - 2 * t], {mat: 0, unlit: true, tint: [1.0, 0.95, 0.84]});
    // rods to the ceiling plate
    for (const x of [L.cx - 1.2, L.cx + 1.2]) {
        for (const z of [L.cz - 3.6, L.cz + 3.6]) {
            b.box([x - 0.06, L.top + 0.12, z - 0.06], [x + 0.06, ROOM.ceiling - 0.25, z + 0.06], {mat: 0, unlit: true, tint: [0.32, 0.27, 0.18]}, ["+y", "-y"]);
        }
    }
}

let lampMaterial: any = null;
let lampMesh: any = null;

const RoomModel = {
    /** Builds the room; add the returned object to the scene. Call update(camera) each frame (the lamp's fade). */
    build(profile: QualityProfile = QUALITY_PROFILES.high): any {
        const root = new THREE.Object3D();
        root.name = "room";
        const vc = THREE.VertexColors;
        const scale = profile.textureScale, detail = profile.roomProps;
        const image = (path: string) => imageTexture(path, scale);
        const drawn = (name: string, draw: () => HTMLCanvasElement, repeat = true) => canvasTexture(name, draw, scale, repeat);
        const materials = [
            new THREE.MeshBasicMaterial({map: image("../textures/room/floor.jpg"), vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: drawn("wallpaper", drawWallpaper), vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: image("../textures/table/wood.jpg"), vertexColors: vc}),
            new THREE.MeshBasicMaterial({color: 0xffffff, vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: drawn("scoreboard", drawScoreboard, false), vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: drawn("sign", drawSign, false), vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: detail ? drawn("painting", drawPainting, false) : null, vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: detail ? drawn("stripe", drawStripeFabric) : null, vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: drawn("tufted", drawTufted), vertexColors: vc}),
            new THREE.MeshBasicMaterial({map: detail ? drawn("photo", drawPhoto, false) : null, vertexColors: vc}),
        ];
        const b = new RoomBuilder(profile.roomSubdivision);
        buildFloorAndCeiling(b);
        buildWalls(b, detail);
        buildDoor(b);
        buildScoreboard(b);
        buildFramedPicture(b, FURNITURE.sign, MAT.sign, detail);
        if (detail) {
            buildFramedPicture(b, FURNITURE.painting, MAT.painting, true);
            buildFramedPicture(b, FURNITURE.photo, MAT.photo, true);
            buildCueRack(b);
            buildChair(b, FURNITURE.chairA, profile.furnitureBevel);
            buildChair(b, FURNITURE.chairB, profile.furnitureBevel);
        }
        buildBench(b, profile.furnitureBevel);
        const room = new THREE.Mesh(b.build(), new THREE.MeshFaceMaterial(materials));
        room.matrixAutoUpdate = false;
        root.add(room);

        const lb = new RoomBuilder(1);
        buildLamp(lb);
        lampMaterial = new THREE.MeshBasicMaterial({color: 0xffffff, vertexColors: vc, transparent: true, opacity: 1});
        lampMesh = new THREE.Mesh(lb.build(), lampMaterial);
        lampMesh.matrixAutoUpdate = false;
        root.add(lampMesh);
        return root;
    },

    /** The lamp fades out as the camera rises towards it (roomLayout.lampOpacity). */
    update(camera: any) {
        if (!lampMesh || !camera || !camera.position) return;
        const o = lampOpacity(camera.position.y);
        lampMaterial.opacity = o;
        lampMesh.visible = o > 0;
    },
};

export default RoomModel;
